import { useState } from "react";
import { useNavigate } from "react-router";
import { Bell, BellRing, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { useUnreadCount } from "../hooks/useUnreadCount";
import { useNotifications, useMarkAllRead, useMarkRead } from "../hooks/useNotifications";
import { NotificationRow } from "./NotificationRow";
import type { NotificationDto } from "../services/notificationsApi";

const DROPDOWN_PAGE_SIZE = 10;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const { data: unreadCount = 0 } = useUnreadCount();
  // Only fetch the dropdown contents when the popover is open.
  const { data: list, isFetching } = useNotifications(
    open ? { pageNumber: 1, pageSize: DROPDOWN_PAGE_SIZE, includeArchived: false } : {}
  );

  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  const handleRowClick = async (n: NotificationDto) => {
    setOpen(false);
    if (!n.isRead) {
      try {
        await markRead.mutateAsync(n.id);
      } catch {
        /* mutation surfaces its own state; navigate regardless so the user isn't blocked */
      }
    }
    if (n.linkUrl) {
      // LinkUrl is a relative app path produced by the backend; the absolute
      // form (with origin) is reserved for emails.
      const target = n.linkUrl.startsWith("http") ? new URL(n.linkUrl).pathname : n.linkUrl;
      navigate(target);
    }
  };

  const Icon = unreadCount > 0 ? BellRing : Bell;
  const badge = unreadCount > 99 ? "99+" : String(unreadCount);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Notifications (${unreadCount} unread)`}>
          <span className="relative inline-flex">
            <Icon className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -right-1.5 -top-1.5 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
                {badge}
              </span>
            )}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" sideOffset={8}>
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-medium">Notifications</span>
          <Button
            variant="ghost"
            size="sm"
            disabled={unreadCount === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            {markAllRead.isPending ? "Marking…" : "Mark all read"}
          </Button>
        </div>
        <Separator />

        <div className="max-h-[420px] overflow-y-auto">
          {isFetching && !list ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (list?.items?.length ?? 0) === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              You're all caught up.
            </div>
          ) : (
            list!.items.map((n) => (
              <NotificationRow key={n.id} notification={n} onClick={handleRowClick} />
            ))
          )}
        </div>

        <Separator />
        <div className="px-3 py-2 text-right">
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
          >
            View all
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
